import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Hand, ScanFace, Hash, Sparkles, Orbit, CalendarClock } from "lucide-react";
import { ASTROLOGY_SERVICES, ASTROLOGY_FAQS } from "@/data/astrologyParayana";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "./landing/WaitlistModal";
import { openBooking } from "@/lib/booking";

const ICONS = {
  palmistry: Hand,
  "face-reading": ScanFace,
  numerology: Hash,
  tarot: Sparkles,
  jataka: Orbit,
  "muhurta-prashna": CalendarClock,
};

const ServiceCard = ({ s, openWaitlist }) => {
  const Icon = ICONS[s.slug] || Sparkles;
  return (
    <article className="flex h-full flex-col rounded-[16px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm transition-shadow hover:shadow-md" data-testid={`astrology-card-${s.slug}`}>
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#B23A2B]/10 text-[#B23A2B]">
          <Icon size={22} strokeWidth={1.8} />
        </span>
        <div>
          <p className="font-baloo text-base text-[#C99B55]">{s.kn}</p>
          <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{s.en}</h3>
          {s.sanskrit && <p className="font-mukta text-xs uppercase tracking-widest text-[#A8722A]">{s.sanskrit}</p>}
        </div>
      </div>
      <p className="mt-3 font-mukta text-sm text-[#6B5044]">{s.desc}</p>
      <ul className="mt-3 flex-1 space-y-1.5">
        {s.points.map((p) => (
          <li key={p} className="flex items-start gap-2 font-mukta text-sm text-[#6B5044]">
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B23A2B]" aria-hidden="true" />{p}
          </li>
        ))}
      </ul>
      <button onClick={() => openWaitlist("Family")} data-testid={`astrology-cta-${s.slug}`}
        className="mt-4 inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#A8722A] hover:text-[#8f5f21]">
        Enquire / early access <ArrowRight size={14} />
      </button>
    </article>
  );
};

export default function AstrologyPage() {
  const [waitlist, setWaitlist] = useState({ open: false, role: "Family" });
  const openWaitlist = () => openBooking({ location: "astrology" });

  useSeo({
    title: "Astrology in Bengaluru — ಜ್ಯೋತಿಷ್ಯ | Palmistry, Face Reading, Numerology, Tarot & Jataka | PurohithConnect",
    description: "Book trusted astrologers in Bengaluru — palmistry (hasta samudrika), face reading, numerology (anka shastra), tarot, Vedic horoscope / jataka (kundali) and muhurta & prashna. Readings in Kannada and English, online or in person.",
    canonicalPath: "/astrology",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "ItemList", name: "Astrology & readings",
        itemListElement: ASTROLOGY_SERVICES.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.en })),
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: ASTROLOGY_FAQS.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Astrology", item: "https://purohithconnect.com/astrology" },
        ],
      },
    ]), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={openWaitlist} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <section className="pt-12 text-center">
          <p className="font-baloo text-base text-[#B23A2B]">ಜ್ಯೋತಿಷ್ಯ</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl" data-testid="astrology-title">Astrology &amp; readings, guided by tradition</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            Clear, practical guidance from trusted readers in Bengaluru — palmistry, face reading, numerology, tarot, Vedic horoscope and muhurta. In your language, online or in person.
          </p>
        </section>

        <section className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="astrology-grid">
          {ASTROLOGY_SERVICES.map((s) => <ServiceCard key={s.slug} s={s} openWaitlist={openWaitlist} />)}
        </section>

        <div className="mt-10 rounded-[14px] border border-[#3E6B4F]/30 p-5 font-mukta text-sm text-[#3E6B4F]" style={{ backgroundColor: "rgba(62,107,79,0.07)" }}>
          Guidance, never fear. Our readers follow the samudrika, anka and jyotisha shastras to help you make decisions with clarity and confidence — and any suggested parihara can be booked with a verified purohith.
        </div>

        <section className="mt-14" data-testid="astrology-faq-section">
          <div className="text-center">
            <p className="font-baloo text-base text-[#B23A2B]">ಪ್ರಶ್ನೋತ್ತರ</p>
            <h2 className="mt-1 font-eczar text-2xl font-semibold text-[#3B221B] md:text-3xl">Astrology &amp; readings — your questions, answered</h2>
          </div>
          <div className="mx-auto mt-6 max-w-3xl space-y-3">
            {ASTROLOGY_FAQS.map((x, i) => (
              <details key={i} className="group rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4 shadow-sm [&_summary::-webkit-details-marker]:hidden" data-testid={`astrology-faq-${i}`} {...(i === 0 ? { open: true } : {})}>
                <summary className="flex cursor-pointer items-center justify-between gap-3 font-eczar text-[15px] font-semibold text-[#3B221B]">
                  {x.q}
                  <ChevronDown size={18} className="shrink-0 text-[#A8722A] transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{x.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link to="/parayana" data-testid="astrology-to-parayana" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Explore group parayana <ArrowRight size={16} /></Link>
          <Link to="/occasions" data-testid="astrology-to-occasions" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Browse pooja occasions <ArrowRight size={16} /></Link>
        </div>

        <div className="mt-12 rounded-[20px] bg-[#3B221B] p-8 text-center md:p-10">
          <p className="font-eczar text-xl font-semibold text-[#FBF6EC]">Want a reading when we launch?</p>
          <p className="mx-auto mt-2 max-w-xl font-mukta text-sm text-[#FBF6EC]/70">Join early access and we'll connect you with a trusted astrologer in Bengaluru — in Kannada, online or in person.</p>
          <button onClick={() => openWaitlist("Family")} data-testid="astrology-cta-band"
            className="mt-5 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]">Get early access</button>
        </div>
      </main>
      <SiteFooter />
      <WaitlistModal key={`${waitlist.open}-${waitlist.role}`} open={waitlist.open} defaultRole={waitlist.role}
        onClose={() => setWaitlist((w) => ({ ...w, open: false }))} />
    </div>
  );
}
